import React, { useState } from 'react';

import Input from '../ui/Input';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { parseApiError } from '../../utils/apiError';

// Edit form for a user's profile fields, role, and active status. `initial`
// seeds the form; `onSubmit(body)` performs the PUT and may throw an axios
// error, mapped to inline field errors via parseApiError. Email/password are
// intentionally not editable here — those are separate self-service flows.
// `roles` is the list of assignable UserType docs ({ _id, Text }) — a
// non-internal viewer never gets 'Internal' in this list.
// `isSelf` disables the role/active controls to match the server's
// self-lockout guard (an admin can't demote or deactivate their own account).
// `locked` disables the entire form to match the server's rule that an
// internal user is only editable by another internal user.
export default function UserForm({ initial, roles, isSelf, locked, onSubmit, onDone, onCancel }) {
  const [values, setValues] = useState({
    fName: initial?.fName ?? '',
    lName: initial?.lName ?? '',
    phone: initial?.phone ?? '',
    active: initial?.active ?? true,
    REF_UserType: initial?.REF_UserType?._id ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const setField = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await onSubmit({
        fName: values.fName,
        lName: values.lName,
        phone: values.phone,
        active: values.active,
        REF_UserType: values.REF_UserType,
      });
      onDone?.();
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe);
      setFieldErrors(fld);
      setSubmitting(false);
    }
  };

  // The current role may not be in `roles` (e.g. 'Internal' is hidden from a
  // non-internal viewer) — add it as a fallback option so the select still
  // displays the true current value instead of silently falling back to
  // whatever option happens to be first.
  const roleOptions =
    initial?.REF_UserType && !roles.some((r) => r._id === initial.REF_UserType._id)
      ? [initial.REF_UserType, ...roles]
      : roles;

  const fieldsDisabled = isSelf || locked;

  return (
    <form className="ss-form" onSubmit={submit} noValidate>
      {formError && <p className="ss-form__error" role="alert">{formError}</p>}

      <div className="ss-field">
        <span className="ss-label">Email</span>
        <p style={{ margin: 0 }}>{initial?.email}</p>
      </div>

      <Input
        label="First name"
        value={values.fName}
        onChange={setField('fName')}
        error={fieldErrors.fName}
        required
        autoFocus
        disabled={locked}
      />
      <Input
        label="Last name"
        value={values.lName}
        onChange={setField('lName')}
        error={fieldErrors.lName}
        required
        disabled={locked}
      />
      <Input
        label="Phone"
        value={values.phone}
        onChange={setField('phone')}
        error={fieldErrors.phone}
        disabled={locked}
      />

      <div className="ss-field">
        <label htmlFor="user-role" className="ss-label">Role</label>
        <select
          id="user-role"
          className="ss-input"
          value={values.REF_UserType}
          onChange={setField('REF_UserType')}
          disabled={fieldsDisabled}
        >
          {roleOptions.map((r) => (
            <option key={r._id} value={r._id}>{r.Text}</option>
          ))}
        </select>
        {fieldErrors.REF_UserType && <p className="ss-field__error">{fieldErrors.REF_UserType}</p>}
      </div>

      <label className="ss-check">
        <input
          type="checkbox"
          checked={values.active}
          disabled={fieldsDisabled}
          onChange={(e) => setValues((v) => ({ ...v, active: e.target.checked }))}
        />
        <span>Active (can sign in)</span>
      </label>

      {isSelf && (
        <p style={{ color: 'var(--ss-brown-hover)', fontSize: '0.85rem', marginTop: '-8px' }}>
          You can't change your own role or active status.
        </p>
      )}
      {locked && (
        <p style={{ color: 'var(--ss-brown-hover)', fontSize: '0.85rem', marginTop: '-8px' }}>
          Only an internal user can edit an internal user.
        </p>
      )}

      <div className="ss-form__actions">
        <Button type="submit" disabled={submitting || locked}>
          {submitting ? <Spinner label="Saving" /> : 'Save user'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        )}
      </div>
    </form>
  );
}
